import { useSystemKeyStatus } from '@/entities/system-key'
import {
  type NotificationVm,
  resolveNotificationLink,
  useDeleteAllNotifications,
  useDeleteNotification,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotificationInfiniteList,
  useNotificationTotalCount,
  useUnreadCount,
} from '@/features/notification'
import { SYSTEM_KEY } from '@/shared/config'
import { CONFIRM_TEXT } from '@/shared/config/confirm-text'
import useDebounce from '@/shared/lib/hooks/use-debounce'
import {
  AlertTriangleOutlined as AlertTriangle,
  CheckCheckOutlined as CheckCheck,
  CheckCircle2Outlined as CheckCircle2,
  InfoCircleOutlined as Info,
  SearchOutlined,
  XCircleOutlined as XCircle,
} from '@/shared/ui/icons'
import UiButton from '@/shared/ui/ui-button'
import UiConfirmModal from '@/shared/ui/ui-confirm-modal'
import UiInput from '@/shared/ui/ui-input'
import UiTabs from '@/shared/ui/ui-tabs'
import UiVirtualList from '@/shared/ui/ui-virtual-list'
import { type ReactNode, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import NotificationItem from './NotificationItem'

/** Icon và lớp CSS theo màu semantic của systemkey NOTIFICATION_SEVERITY (không khoá theo giá trị enum) */
const SEVERITY_ICON_BY_COLOR: Record<string, { icon: ReactNode; tone: string }> = {
  processing: { icon: <Info size={18} />, tone: 'info' },
  success: { icon: <CheckCircle2 size={18} />, tone: 'success' },
  warning: { icon: <AlertTriangle size={18} />, tone: 'warning' },
  error: { icon: <XCircle size={18} />, tone: 'error' },
}

type TabKey = 'all' | 'unread'

const SEARCH_DEBOUNCE_MS = 300
const MIN_KEYWORD_LENGTH = 2

const NotificationList = () => {
  const navigate = useNavigate()
  const severityStatus = useSystemKeyStatus(SYSTEM_KEY.NOTIFICATION_SEVERITY)
  const [tab, setTab] = useState<TabKey>('all')
  const [searchText, setSearchText] = useState('')
  const [revealedId, setRevealedId] = useState<string | null>(null)

  // Từ khoá ngắn hơn hai ký tự thì không gửi (bỏ lọc)
  const debounced = useDebounce(searchText, SEARCH_DEBOUNCE_MS).trim()
  const keyword = debounced.length >= MIN_KEYWORD_LENGTH ? debounced : undefined

  // Đổi tab hoặc từ khoá → query key đổi → tải lại từ trang đầu
  const { data, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useNotificationInfiniteList({ isRead: tab === 'unread' ? false : undefined, keyword })
  const { data: totalCount = 0 } = useNotificationTotalCount()
  const { data: unreadCount = 0 } = useUnreadCount()
  const { mutate: markRead } = useMarkNotificationRead()
  const { mutate: markAllRead, isPending: isMarkingAll } = useMarkAllNotificationsRead()
  const { mutate: deleteOne } = useDeleteNotification()
  const { mutate: deleteAll } = useDeleteAllNotifications()

  const items: NotificationVm[] = data?.pages.flatMap((page) => page.object?.items ?? []) ?? []

  const handleTabChange = (key: string) => {
    setTab(key as TabKey)
    setRevealedId(null)
  }

  const handleClick = (item: NotificationVm) => {
    if (!item.isRead) markRead(item.id)
    // Chỉ điều hướng nội bộ; liên kết ngoài không tự mở
    const target = resolveNotificationLink(item.linkUrl)
    if (target) navigate(target)
  }

  const handleDelete = (id: string) => {
    setRevealedId(null)
    deleteOne(id)
  }

  const handleDeleteAll = () =>
    UiConfirmModal({
      title: 'Xóa tất cả thông báo',
      titleHtml:
        'Chỉ xóa các thông báo cá nhân của bạn. Thông báo gửi theo vai trò hoặc gửi chung vẫn được giữ lại.',
      icon: 'trashRed',
      okText: CONFIRM_TEXT.delete,
      cancelText: CONFIRM_TEXT.cancel,
      okButtonProps: { danger: true },
      onOk: () => deleteAll(),
    })

  let emptyText = 'Không có thông báo'
  if (keyword) emptyText = 'Không tìm thấy thông báo phù hợp'
  else if (tab === 'unread') emptyText = 'Không có thông báo chưa đọc'

  return (
    <div className="notification-list">
      <div className="notification-list-header">
        <span>Thông báo</span>
        <UiButton
          type="link"
          variant="link"
          size="small"
          icon={<CheckCheck size={18} />}
          disabled={unreadCount === 0}
          loading={isMarkingAll}
          onClick={() => markAllRead()}
        >
          Đánh dấu đã đọc
        </UiButton>
      </div>
      <div className="notification-list-search">
        <UiInput
          allowClear
          placeholder="Tìm kiếm thông báo"
          aria-label="Tìm kiếm thông báo"
          prefixIcon={<SearchOutlined />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
      </div>
      <UiTabs
        className="notification-list-tabs"
        activeKey={tab}
        onChange={handleTabChange}
        items={[
          { key: 'all', label: `Tất cả (${totalCount})` },
          { key: 'unread', label: `Chưa đọc (${unreadCount})` },
        ]}
      />
      <UiVirtualList<NotificationVm>
        data={items}
        rowKey={(item) => item.id}
        loading={isLoading}
        hasMore={hasNextPage}
        isFetchingMore={isFetchingNextPage}
        onEndReached={() => fetchNextPage()}
        emptyText={emptyText}
        estimateSize={88}
        maxHeight="none"
        testId="notification-virtual-list"
        renderItem={(item) => (
          <NotificationItem
            item={item}
            severityTone={
              SEVERITY_ICON_BY_COLOR[severityStatus.getColor(item.severity)] ??
              SEVERITY_ICON_BY_COLOR.processing
            }
            revealed={revealedId === item.id}
            onRevealChange={setRevealedId}
            onClick={handleClick}
            onDelete={handleDelete}
          />
        )}
      />
      <div className="notification-list-footer">
        <UiButton
          type="link"
          variant="link"
          size="small"
          danger
          disabled={totalCount === 0}
          onClick={handleDeleteAll}
        >
          Xóa tất cả
        </UiButton>
      </div>
    </div>
  )
}

export default NotificationList
